import type { ReactElement, ChangeEvent, MouseEvent } from "react";
import type { LucideIcon } from "lucide-react";

import { useState, useRef } from "react";
import {
  User,
  Edit3,
  Trash2,
  Star,
  Shield,
  Heart,
  Sparkles,
  Users,
  Upload,
  Wand2,
  Camera,
  Loader2,
  X,
  AlertTriangle,
  Eye,
  Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Character role type
 */
type CharacterRole =
  | "protagonist"
  | "supporting"
  | "antagonist"
  | "mentor"
  | "sidekick";

/**
 * Character data structure
 */
interface Character {
  id: string;
  name: string;
  role: CharacterRole;
  description?: string;
  portrait_url?: string;
  traits?: string[];
}

/**
 * CharacterCard component props
 */
interface CharacterCardProps {
  character: Character;
  onEdit?: () => void;
  onDelete?: () => void;
  onUploadPortrait?: (characterId: string, file: File) => void;
  onGeneratePortrait?: (characterId: string) => void;
  onViewPortrait?: (url: string, name: string) => void;
  onPickPortrait?: (character: Character) => void;
  portraitLoading?: boolean;
  hasExistingBooks?: boolean;
  viewOnly?: boolean;
}

/**
 * Role configuration
 */
interface RoleConfig {
  icon: LucideIcon;
  color: string;
  bg: string;
  label: string;
}

const ROLE_CONFIG: Record<CharacterRole, RoleConfig> = {
  protagonist: {
    icon: Star,
    color: "#F59E0B",
    bg: "#FEF3C7",
    label: "Protagonist",
  },
  supporting: {
    icon: Heart,
    color: "#EC4899",
    bg: "#FCE7F3",
    label: "Supporting",
  },
  antagonist: {
    icon: Shield,
    color: "#EF4444",
    bg: "#FEE2E2",
    label: "Antagonist",
  },
  mentor: {
    icon: Sparkles,
    color: "#8B5CF6",
    bg: "#EDE9FE",
    label: "Mentor",
  },
  sidekick: {
    icon: Users,
    color: "#22C55E",
    bg: "#DCFCE7",
    label: "Sidekick",
  },
};

/**
 * Helper to get full image URL
 */
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith("/static/")) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

/**
 * CharacterCard Component
 *
 * Displays a character with portrait, role, description, and traits.
 * Supports portrait upload, generation, and editing.
 */
const CharacterCard = ({
  character,
  onEdit,
  onDelete,
  onUploadPortrait,
  onGeneratePortrait,
  onViewPortrait,
  onPickPortrait,
  portraitLoading = false,
  hasExistingBooks = false,
  viewOnly = false,
}: CharacterCardProps): ReactElement => {
  const [showPortraitOptions, setShowPortraitOptions] =
    useState<boolean>(false);
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const role = ROLE_CONFIG[character.role] || ROLE_CONFIG.supporting;
  const RoleIcon = role.icon;

  // Handle photo selection - just create preview, don't upload yet
  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a JPEG, PNG, or WebP image.");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Maximum file size is 10MB.");
      return;
    }

    // Create preview
    const previewUrl = URL.createObjectURL(file);
    setPendingPhoto(file);
    setPendingPreview(previewUrl);
    setShowPortraitOptions(false);
  };

  // Confirm and start transformation
  const handleConfirmTransform = (): void => {
    if (pendingPhoto && onUploadPortrait) {
      onUploadPortrait(character.id, pendingPhoto);
      // Clean up
      if (pendingPreview) {
        URL.revokeObjectURL(pendingPreview);
      }
      setPendingPhoto(null);
      setPendingPreview(null);
    }
  };

  // Cancel pending photo
  const handleCancelPending = (): void => {
    if (pendingPreview) {
      URL.revokeObjectURL(pendingPreview);
    }
    setPendingPhoto(null);
    setPendingPreview(null);
  };

  const handleGenerateClick = (): void => {
    if (onGeneratePortrait) {
      onGeneratePortrait(character.id);
      setShowPortraitOptions(false);
    }
  };

  // Close dropdown when clicking outside
  const handleCardClick = (e: MouseEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement;
    if (showPortraitOptions && !target.closest(".portrait-area")) {
      setShowPortraitOptions(false);
    }
  };

  // If we have a pending photo, show the preview/confirm UI
  if (pendingPreview) {
    return (
      <div
        className="relative p-4 rounded-2xl dark:bg-amber-900/20"
        style={{ background: "#FFFBF5", border: "2px solid #F59E0B" }}
      >
        <div className="flex gap-4">
          {/* Preview Image */}
          <div className="relative flex-shrink-0">
            <img
              src={pendingPreview}
              alt="Preview"
              className="w-24 h-24 rounded-xl object-cover"
              style={{ border: "2px solid #F59E0B" }}
            />
            <button
              onClick={handleCancelPending}
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full shadow-md flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              style={{ background: "var(--st-bg-surface)", border: "1px solid #E5E7EB" }}
            >
              <X className="w-3.5 h-3.5" style={{ color: "#6B7280" }} />
            </button>
          </div>

          {/* Confirmation Content */}
          <div className="flex-1 min-w-0">
            <h4
              className="font-bold text-base mb-1"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
            >
              {character.name}
            </h4>
            <p className="text-sm mb-3" style={{ color: "#92400E" }}>
              Ready to transform into storybook style
            </p>

            {/* Warning if has existing books */}
            {hasExistingBooks && (
              <div
                className="flex items-start gap-2 mb-3 p-2 rounded-lg"
                style={{ background: "#FEF3C7" }}
              >
                <AlertTriangle
                  className="w-4 h-4 flex-shrink-0 mt-0.5"
                  style={{ color: "#D97706" }}
                />
                <p className="text-xs" style={{ color: "#92400E" }}>
                  This will update the character for future books. Existing
                  books won't be affected.
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={handleCancelPending}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:opacity-80"
                style={{ background: "var(--st-bg-muted)", color: "var(--st-text-muted)" }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmTransform}
                className="px-4 py-1.5 rounded-lg text-sm font-medium text-white flex items-center gap-1.5 transition-all hover:scale-105"
                style={{
                  background:
                    "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                }}
              >
                <Sparkles className="w-3.5 h-3.5" />
                Transform
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (portraitLoading) {
    return (
      <div
        className="relative p-4 rounded-2xl"
        style={{ background: "var(--st-bg-elevated)", border: "1px solid var(--st-border-light)" }}
      >
        <div className="flex gap-4 items-center">
          <div
            className="w-20 h-20 rounded-xl flex items-center justify-center"
            style={{
              background: "linear-gradient(145deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)",
            }}
          >
            <Loader2
              className="w-8 h-8 animate-spin"
              style={{ color: "var(--st-primary)" }}
            />
          </div>
          <div className="flex-1">
            <h4
              className="font-bold text-base mb-1"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
            >
              {character.name}
            </h4>
            <p className="text-sm" style={{ color: "var(--st-text-muted)" }}>
              Transforming photo into storybook style...
            </p>
            <div
              className="mt-2 h-1.5 rounded-full overflow-hidden w-32"
              style={{ background: "var(--st-border-light)" }}
            >
              <div
                className="h-full rounded-full animate-pulse"
                style={{
                  width: "60%",
                  background:
                    "linear-gradient(90deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative p-4 rounded-2xl transition-all hover:shadow-md group"
      style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
      onClick={handleCardClick}
    >
      <div className="flex gap-4">
        {/* Portrait - Larger & Clickable */}
        <div className="portrait-area relative flex-shrink-0">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            className="hidden"
          />
          <div
            className="w-20 h-20 rounded-xl overflow-hidden cursor-pointer group/portrait relative"
            style={{
              background: "linear-gradient(145deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)",
            }}
            onClick={(e: MouseEvent<HTMLDivElement>) => {
              e.stopPropagation();
              // If portrait exists
              if (character.portrait_url) {
                // If picker is available and not view-only, open picker
                if (onPickPortrait && !viewOnly) {
                  onPickPortrait(character);
                } else if (onViewPortrait) {
                  // Otherwise just view it enlarged
                  onViewPortrait(
                    getImageUrl(character.portrait_url) || "",
                    character.name
                  );
                }
              } else if (!viewOnly) {
                // No portrait - open options menu to create one
                setShowPortraitOptions(!showPortraitOptions);
              }
            }}
          >
            {character.portrait_url ? (
              <>
                <img
                  src={getImageUrl(character.portrait_url) || undefined}
                  alt={character.name}
                  className="w-full h-full object-cover"
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover/portrait:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover/portrait:opacity-100">
                  {onPickPortrait && !viewOnly ? (
                    <ImageIcon className="w-5 h-5 text-white" />
                  ) : (
                    <Eye className="w-5 h-5 text-white" />
                  )}
                </div>
                {/* Change portrait button - only visible if not view-only */}
                {!viewOnly && (
                  <button
                    onClick={(e: MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      // If picker is available, open it; otherwise show dropdown
                      if (onPickPortrait) {
                        onPickPortrait(character);
                      } else {
                        setShowPortraitOptions(!showPortraitOptions);
                      }
                    }}
                    className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full shadow-lg flex items-center justify-center hover:bg-orange-50 dark:hover:bg-orange-900/30 hover:scale-110 transition-all z-10"
                    style={{ background: "var(--st-bg-surface)", border: "2px solid var(--st-primary)" }}
                    title="Change portrait"
                  >
                    <Camera className="w-4 h-4" style={{ color: "var(--st-primary)" }} />
                  </button>
                )}
              </>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center group-hover/portrait:bg-black/5 transition-all">
                <User className="w-8 h-8 mb-1" style={{ color: "var(--st-border-medium)" }} />
                {!viewOnly && (
                  <>
                    <Camera
                      className="w-6 h-6 mb-1 hidden group-hover/portrait:block"
                      style={{ color: "var(--st-primary)" }}
                    />
                    <span
                      className="text-xs hidden group-hover/portrait:block"
                      style={{
                        color: "var(--st-primary)",
                        fontFamily: "Quicksand, sans-serif",
                      }}
                    >
                      Add photo
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Portrait Options Dropdown - only show if not view-only */}
          {showPortraitOptions && !viewOnly && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-40"
                onClick={(e: MouseEvent<HTMLDivElement>) => {
                  e.stopPropagation();
                  setShowPortraitOptions(false);
                }}
              />
              <div
                className="absolute top-full left-0 mt-2 w-56 rounded-xl shadow-xl z-50 overflow-hidden"
                style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
                onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
              >
                <div
                  className="p-3 border-b"
                  style={{ borderColor: "var(--st-border-light)", background: "var(--st-bg-muted)" }}
                >
                  <p
                    className="text-xs font-semibold"
                    style={{ color: "var(--st-text-heading)" }}
                  >
                    {character.portrait_url
                      ? "Change Portrait"
                      : "Add Character Portrait"}
                  </p>
                </div>

                {/* Warning if has existing books */}
                {hasExistingBooks && character.portrait_url && (
                  <div
                    className="px-3 py-2 border-b dark:bg-amber-900/30"
                    style={{ background: "#FFFBEB", borderColor: "var(--st-border-light)" }}
                  >
                    <div className="flex items-start gap-2">
                      <AlertTriangle
                        className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                        style={{ color: "#D97706" }}
                      />
                      <p className="text-xs" style={{ color: "#92400E" }}>
                        Changing portrait affects future books only
                      </p>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-orange-50 dark:hover:bg-orange-900/30 transition-colors text-left"
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: "var(--st-bg-elevated)" }}
                  >
                    <Upload className="w-4 h-4" style={{ color: "var(--st-primary)" }} />
                  </div>
                  <div>
                    <p
                      className="text-sm font-medium"
                      style={{ color: "var(--st-text-heading)" }}
                    >
                      Upload Photo
                    </p>
                    <p className="text-xs" style={{ color: "#9CA3AF" }}>
                      Transform your photo to art
                    </p>
                  </div>
                </button>
                <button
                  onClick={handleGenerateClick}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors text-left border-t"
                  style={{ borderColor: "var(--st-border-light)" }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center dark:bg-purple-900/40"
                    style={{ background: "#F3E8FF" }}
                  >
                    <Wand2 className="w-4 h-4" style={{ color: "#8B5CF6" }} />
                  </div>
                  <div>
                    <p
                      className="text-sm font-medium"
                      style={{ color: "var(--st-text-heading)" }}
                    >
                      Generate with AI
                    </p>
                    <p className="text-xs" style={{ color: "#9CA3AF" }}>
                      Create from description
                    </p>
                  </div>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <h4
              className="font-bold text-base"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
            >
              {character.name}
            </h4>
            <span
              className="px-2 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1"
              style={{ background: role.bg, color: role.color }}
            >
              <RoleIcon className="w-3 h-3" />
              {role.label}
            </span>
          </div>

          {character.description && (
            <p
              className="text-sm line-clamp-2 mb-2"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
            >
              {character.description}
            </p>
          )}

          {/* Traits */}
          {character.traits && character.traits.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {character.traits.slice(0, 4).map((trait, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 rounded-md text-xs font-medium"
                  style={{ background: "var(--st-bg-muted)", color: "var(--st-text-muted)" }}
                >
                  {trait}
                </span>
              ))}
              {character.traits.length > 4 && (
                <span
                  className="text-xs font-medium"
                  style={{ color: "#9CA3AF" }}
                >
                  +{character.traits.length - 4}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions - Always visible on mobile, hover on desktop */}
        {(onEdit || onDelete) && (
          <div className="flex flex-col gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
            {onEdit && (
              <button
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Edit character"
              >
                <Edit3 className="w-4 h-4" style={{ color: "#9CA3AF" }} />
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                title="Remove character"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CharacterCard;
